// lib/validations/auth.ts
// Validação de autenticação usando Zod
// Referência: docs/SECURITY_AUDIT_SESSION_HIJACKING.md

import { z } from "zod"

/** Domains allowed to create user accounts via signup */
export const ALLOWED_SIGNUP_DOMAINS = [
  "azimutwealth.com.br",
  "azimutknox.com.br",
  "azimutbrasil.com",
  "lacazmartins.com.br",
  "azguidance.com.br",
  "knoxcapital.com.br",
  "unifinance.com.br",
]

/** Specific emails allowed outside the domain allowlist */
export const ALLOWED_SIGNUP_EMAILS = ["herbertst@gmail.com"]

export function isAllowedSignupEmail(email: string): boolean {
  const normalizedEmail = email.toLowerCase().trim()
  const emailDomain = normalizedEmail.split("@")[1]

  if (!emailDomain) {
    return false
  }

  return (
    ALLOWED_SIGNUP_DOMAINS.includes(emailDomain) ||
    ALLOWED_SIGNUP_EMAILS.includes(normalizedEmail)
  )
}

/**
 * Schema de registro - validação robusta de senha com confirmação
 */
export const signUpSchema = z
  .object({
    email: z
      .string()
      .min(1, "Email é obrigatório")
      .email("Email inválido")
      .max(255, "Email muito longo")
      .toLowerCase()
      .trim(),
    password: z
      .string()
      .min(8, "Senha deve ter no mínimo 8 caracteres")
      .max(100, "Senha muito longa")
      .regex(/[0-9]/, "Senha deve conter pelo menos um número")
      .regex(/[^a-zA-Z0-9]/, "Senha deve conter pelo menos um caractere especial")
      .regex(/[a-zA-Z]/, "Senha deve conter pelo menos uma letra"),
    confirmPassword: z
      .string()
      .min(1, "Confirmação de senha é obrigatória"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "As senhas não coincidem",
    path: ["confirmPassword"], // Anexa o erro ao campo confirmPassword
  })

/**
 * Schema de login - validação básica
 */
export const signInSchema = z.object({
  email: z
    .string()
    .min(1, "Email é obrigatório")
    .email("Email inválido")
    .toLowerCase()
    .trim(),
  password: z
    .string()
    .min(1, "Senha é obrigatória"),
})

/**
 * Tipos TypeScript derivados dos schemas
 */
export type SignUpInput = z.infer<typeof signUpSchema>
export type SignInInput = z.infer<typeof signInSchema>

/**
 * Helper para validar dados com Zod
 * Retorna resultado tipado ou erro formatado
 * Pode ser usado por todos os módulos de validação
 */
export function validateWithZod<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown
): { success: true; data: z.infer<T> } | { success: false; error: string } {
  try {
    const validated = schema.parse(data)
    return { success: true, data: validated }
  } catch (error) {
    if (error instanceof z.ZodError) {
      // ZodError usa 'issues' para armazenar os erros de validação
      if (error.issues && Array.isArray(error.issues) && error.issues.length > 0) {
        const firstIssue = error.issues[0]
        return { 
          success: false, 
          error: firstIssue?.message || "Dados inválidos" 
        }
      }
      // Fallback se issues estiver vazio (não deveria acontecer, mas por segurança)
      return { 
        success: false, 
        error: "Dados inválidos" 
      }
    }
    
    // Se não for ZodError, tenta extrair mensagem do erro genérico
    if (error instanceof Error) {
      return { 
        success: false, 
        error: error.message || "Erro de validação desconhecido" 
      }
    }
    
    return { success: false, error: "Erro de validação desconhecido" }
  }
}

